import { AppContext } from "App";

import { LazyLoadingSectionLogger, LoggingSection } from "@multiweb/logging";
import { FileUtils } from "Global/Base/Utils/FileUtils";
import { Event } from "../../Global/Base/Events";
import { contextlessInstanceOf } from "../../Global/Base/Polyfill/instanceOf";
import { PromiseUtils } from "../../Global/Base/Promises/PromiseUtils";
import { IFileStreamReader } from "../../Global/Base/Streams/IFileStreamReader";
import { UrlUtils } from "../../Global/Base/Utils/UrlUtils";
import { EDMSException, OWExceptions } from "../Basic/OWExceptions";
import { IUploadData, IWorkerInitializationMessageData } from "../Workers/Uploader/UploadWorkerData";

/**
     * Třída reprezentuje data a stav uploadu jednoho souboru
     */
class FileUploadData {
    /**
     * Unikátní identifikátor nahrávaného souboru pro komunikaci s workerem
     */
    public readonly id: number;
    /**
     * Reader s nahrávanými daty souboru
     */
    public streamReader: IFileStreamReader;
    /**
     * Značí zda uživatel zrušil nahrávání
     */
    public canceled: boolean;
    /**
     * Index (zero-based) posledního nahraného bajtu
     */
    public lastByteRead: number;

    /**
     * Funkce poskytující hash souboru
     */
    public hashProvider?: () => string;

    public readonly finishedHandler: (documentId: string) => void;
    public readonly errorHandler: (exception: Error) => void;
    public readonly progressHandler: (progress: number) => void;

    constructor(id: number, progressHandler: (progress: number) => void, errorHandler: (exception: Error) => void, finishedHandler: (documentId: string) => void) {
        this.finishedHandler = finishedHandler;
        this.errorHandler = errorHandler;
        this.progressHandler = progressHandler;
        this.id = id;
    }

    /**
     * Nastaví na výchozí hodnoty
     */
    public init(): void {
        this.canceled = false;
        this.lastByteRead = -1;
        this.streamReader = null;
    }
}

export class UploadCancelledException extends Error {
    constructor(message?: string) {
        super(message);
    }

    public static override get [Symbol.hasInstance](): (instance: any) => boolean {
        return (instance: any) => instance != null && contextlessInstanceOf(instance, UploadCancelledException);
    }
}

export class FileUploader {
    private _isComplete: boolean = false;
    private _isStarted: boolean = false;
    private _isError: boolean = false;
    private _documentId: string = "";
    private _currentProgress: number = 0;
    private _finishDelay: number = 2000;

    private static _logger = LazyLoadingSectionLogger.create(LoggingSection.upload);
    private static _workerLoader: Promise<Worker> = null;

    /**
     * Maximální velikost dat, které jsou zpracovány v jednom kroku
     */
    private static _chunkSize: number = 1048576 * 2;
    /**
     * Web Worker zpracovávající upload
     */
    private static _worker: Worker;
    /**
     * Maximální doposud použité id
     */
    private static _maxId = 0;
    /**
     * Kolekce se všemi aktuálně nahrávanými soubory
     */
    private static _files = new Map<number, FileUploadData>();
    /**
     * Identifikátor tohoto uploadu
     */
    private _id = 0;
    /**
     * Název nahrávaného souboru
     */
    private _fileName = "";
    /**
     * Informace o současném uploadu
     */
    private _fileData: FileUploadData;

    /**
     * Událost vyvolaná při změně progressu nahrávání souboru - hodnoty 0 - 100
     */
    public onProgress: Event<FileUploader, number> = new Event<FileUploader, number>();
    /**
     * Událost vyvolaná při dokončení nahrávání souboru - atribut obsahuje identifikátor pro nový OWDocument
     */
    public onFinished: Event<FileUploader, string> = new Event<FileUploader, string>();
    /**
     * Událost vyvolaná při zrušení nahrávání souboru
     */
    public onCancel: Event<FileUploader, UploadCancelledException> = new Event<FileUploader, UploadCancelledException>();
    /**
     * Událost vyvolaná při chybě při nahrávání
     */
    public onError: Event<FileUploader, Error> = new Event<FileUploader, Error>();
    /**
     * Událost vyvolaná při startu nahrávání
     */
    public onStart: Event<FileUploader, Error> = new Event<FileUploader, Error>();

    /**
     * Informace o tom, zda je upload dokončen
     */
    public get isComplete(): boolean {
        return this._isComplete;
    }

    /**
     * Informace o tom, zda byl upload zahajen
     */
    public get isStarted(): boolean {
        return this._isStarted;
    }

    /**
     * Informace o tom, zda skoncil s chybou
     */
    public get isError(): boolean {
        return this._isError;
    }

    /**
     * Informace o tom, zda je upload stále aktivní - buď právě probíhá nebo je v chybovém stavu
     */
    public get isActive(): boolean {
        return this.isStarted && (!this.isComplete || this.isError);
    }

    /**
     * Aktuální procentuální stav nahrávání
     */
    public get currentProgress(): number {
        return this._currentProgress;
    }

    /**
     * Id nahraného dokumentu, dostupné až po dokončení nahrávání
     */
    public get documentId(): string {
        return this._documentId;
    }

    /**
     * Nastaví, jak dlouho se má po dokončení nahrávání upload tvářit jako nedokončený
     * @param delay čas v ms
     */
    public set finishDelay(delay: number) {
        this._finishDelay = delay;
    }

    public get finishDelay(): number {
        return this._finishDelay;
    }

    /**
     * Název nahrávaného souboru
     */
    public get fileName(): string {
        return this._fileName;
    }

    /**
     * Nahrávaný soubor
     */
    public get fileStreamReader(): IFileStreamReader {
        return this._fileData.streamReader;
    }

    constructor() {
        FileUploader._initWorker();

        // vygeneruje se nové id
        this._id = ++FileUploader._maxId;

        // vytvoří se data uploadu a uloží se
        this._fileData = new FileUploadData(this._id,
            (progress) => {
                this._currentProgress = progress;
                if (!this._isComplete)
                    this.onProgress.trigger(this, progress);
            },
            (error) => {
                this._isError = true;
                this._isComplete = true;
                this.onError.trigger(this, error);
            },
            (documentId) => {
                this._documentId = documentId;
                // upload se ještě chvíli tváří jako nedokončený - zabraňuje předčasnému uložení
                if (this._finishDelay) {
                    PromiseUtils.delay(this._finishDelay).then(() => {
                        this._isComplete = true;
                        this.onFinished.trigger(this, documentId);
                    });
                } else {
                    this._isComplete = true;
                    this.onFinished.trigger(this, documentId);
                }
            });
    }

    /**
     * Nastaví na výchozí hodnoty
     */
    private _init(): void {
        this._fileData.init();

        this._fileName = "";
        this._isComplete = false;
        this._isStarted = false;
        this._isError = false;
        this._documentId = "";
        this._currentProgress = 0;
    }

    /**
     * Spustí nahrávání streamu dat
     * @param streamReader reader umožňující číst stream s nahrávaným souborem
     * @param uploadUrlFunction funkce, která vrátí url pro upload
     * @param hashProvider funkce, která vrátí hash nahrávaného souboru
     */
    public async start(streamReader: IFileStreamReader, uploadUrlFunction: () => Promise<string>, hashProvider?: () => string): Promise<void> {
        this._init();

        this._fileData.streamReader = streamReader;
        this._fileData.hashProvider = hashProvider;
        this._fileName = streamReader.name;
        this._isStarted = true;

        FileUploader._files.set(this._id, this._fileData);

        // událost je potřeba vyvolat i pokud se nahrávání nespustí - mohou na to reagovat další komponenty
        this.onStart.trigger(this, null);

        let uploadUrl: string;
        try {
            uploadUrl = await uploadUrlFunction();
        } catch (e) {
            // je potřeba iniciovat upload i pokud došlo k chybě při získávání url (zapamatování posledního nahrávaného souboru)
            this._fileData.errorHandler(e);
            return;
        }

        // a worker se nakonfiguruje pro nový upload
        await FileUploader._workerLoader;
        FileUploader._worker.postMessage({
            type: "configuration",
            data: {
                id: this._fileData.id,
                url: `${uploadUrl}&fileName=${encodeURIComponent(streamReader.name)}`,
                eTag: UrlUtils.getParameterByName("eTag", uploadUrl),
                fileSize: streamReader.size,
            } as IUploadData,
        });

        // pošle první kus souboru ke zpracování
        FileUploader._sendChunkToWorker(this._fileData);
    }

    /**
     * Načte se další kus souboru a odešle se do workeru ke zpracování a uploadu
     * @param fileData
     */
    private static async _sendChunkToWorker(fileData: FileUploadData): Promise<void> {
        if (fileData.canceled) {
            FileUploader._files.delete(fileData.id);
            return;
        }

        const startByte = fileData.lastByteRead + 1;
        fileData.lastByteRead = startByte + FileUploader._chunkSize < fileData.streamReader.size ? startByte + FileUploader._chunkSize - 1 : fileData.streamReader.size - 1;

        try {
            // načte se kus souboru
            const chunk = await fileData.streamReader.read(startByte, fileData.lastByteRead);

            // a pošle se workeru
            await FileUploader._workerLoader;
            FileUploader._worker.postMessage({
                type: "upload",
                id: fileData.id,
                chunk: chunk,
                startByte: startByte,
                endByte: fileData.lastByteRead,
            }, [chunk]);
        } catch (e) {
            FileUploader._showErrorAndDispose(fileData, e);
        }
    }

    /**
     * Přeruší nahrávání souboru (pokud probíhá) a vyresetuje všechny hodnoty
     */
    public async cancel(): Promise<void> {
        if (!this._isStarted)
            return;

        // vyresetuje všechny hodnoty na počáteční
        this._init();
        this.onCancel.trigger(this, new UploadCancelledException());

        const fileData = FileUploader._files.get(this._id);
        if (fileData == null || fileData.canceled)
            return;

        fileData.canceled = true;
        await FileUploader._workerLoader;
        FileUploader._worker.postMessage({
            type: "cancel",
            id: fileData.id,
        });
    }

    /**
     * Při prvním volání vytvoří Worker a ziniciuje ho
     */
    private static _initWorker(): Promise<Worker> {
        if (FileUploader._worker)
            return Promise.resolve(FileUploader._worker);

        if (FileUploader._workerLoader != null)
            return FileUploader._workerLoader;

        const prom = PromiseUtils.defer<Worker>();
        FileUploader._workerLoader = prom.promise;

        // jeden worker je sdílený pro všechny uploady
        const worker = new Worker("js/TescoSW.Worker.Upload.Loader.js");

        worker.onerror = (e) => {
            FileUploader._logger.error(e);
            prom.reject(e);
        };
        worker.onmessage = (e) => {
            if (e.data == "WorkerLoaded") {
                FileUploader._worker = worker;
                prom.resolve();
                FileUploader._worker.onmessage = FileUploader._workerMessage;
                FileUploader._worker.onerror = FileUploader._workerError;

                FileUploader._worker.postMessage({
                    type: "initialize",
                    data: {
                        loggingSettings: AppContext.instance.configuration.logging,
                    } as IWorkerInitializationMessageData,
                });
            }
        };

        return prom.promise;
    }

    /**
     * Přijatá zpráva z workeru
     */
    private static _workerMessage(e: MessageEvent): void {
        const msgData = e.data;
        const fileData = FileUploader._files.get(msgData.id);

        if (fileData == null) {
            FileUploader._logger.error(`Upload data for id ${msgData.id} not found`);
            return;
        }

        switch (msgData.status) {
            case "complete": {
                FileUploader._files.delete(fileData.id);
                const documentId = FileUtils.trimFileName(`${msgData.eTag}:${fileData.hashProvider ? fileData.hashProvider() : ""}:${fileData.streamReader.name}`);
                fileData.finishedHandler(documentId);
                return;
            }
            case "error": {
                const owErr = OWExceptions.getRegisteredExceptionType(e.data.typeName);
                const err = owErr ? new owErr(msgData.errorText) : new Error(msgData.errorText);
                if (err instanceof EDMSException && Reflect.has(msgData, "locKey"))
                    err.locKey = msgData.locKey;
                FileUploader._showErrorAndDispose(fileData, err);
                return;
            }
            case "process": {
                const progress = Math.round(((msgData.uploadedByte + 1) / fileData.streamReader.size) * 100);
                fileData.progressHandler(progress);

                if (msgData.uploadedByte + 1 !== fileData.streamReader.size)
                    FileUploader._sendChunkToWorker(fileData);
                return;
            }
        }
    }

    /**
     * Přijatá chyba z workeru
     */
    private static _workerError(e: ErrorEvent): void {
        let er: Error;
        //pokud nastane chyba ve WebWorkeru tak je "e" typy ErrorEvent a prijdou dalsi informace
        //pokud se ale nepovede nacist JS soubor Workeru, tak je "e" jen typu Event a neprijdou zadne dodatecne informace
        //testovano na IE 11 a Chrome 46
        if (e.message) {
            er = new Error(e.message);
            (er as any).originalSource = `${e.filename}:${e.lineno || 0}:${e.colno || 0}`;
        } else {
            er = new Error("Can't load Workers.Uploader.js");
        }

        throw er;
    }

    /**
     * Zavolá error handler, odstraní upload data z kolekce
     * @param fileData
     * @param error
     */
    private static _showErrorAndDispose(fileData: FileUploadData, error: Error): void {
        FileUploader._files.delete(fileData.id);
        fileData.errorHandler(error);
    }
}
